import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section ref={ref} className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://static.readdy.ai/image/18ca25ea8a69d5eb1e849ccadbb7687f/ea44ee3eb5f3c7c6a9218711d60270e0.jpeg"
          alt="Modern furnished living space interior"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 via-navy-900/75 to-navy-800/40" />
      </div>

      {/* Floating badge */}
      <div className="absolute top-36 right-8 lg:right-16 bg-gold-500/90 backdrop-blur-sm text-navy-900 px-4 py-3 rounded text-center hidden lg:block">
        <p className="font-bold text-xl leading-none">All-Inclusive</p>
        <p className="text-xs font-medium mt-1">Bills &bull; Wi-Fi &bull; Cleaning</p>
      </div>

      {/* Content */}
      <div className={`relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-0.5 bg-gold-400" />
            <span className="text-gold-400 text-sm font-medium tracking-widest uppercase">Regional &amp; Metro Australia</span>
          </div>
          <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
            Comfortable Living,<br />
            <span className="text-gold-400 italic">Hassle-Free.</span>
          </h1>
          <p className="text-white/75 text-lg lg:text-xl leading-relaxed mb-10 max-w-xl">
            Premium furnished accommodation with all utilities included. Clean, reliable, and conveniently located across regional towns and metro cities including Sydney, Brisbane, and Melbourne.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://rentals.galaxyhousing.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold text-base px-8 py-4 rounded transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-heart-line" />
              </div>
              View Available Rentals
            </a>
          </div>

          {/* Quick stats */}
          <div className="flex gap-8 mt-12 pt-10 border-t border-white/20">
            {[
              { number: '10+', label: 'Years Experience' },
              { number: '3206+', label: 'Happy Residents' },
              { number: '100%', label: 'Bills Included' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading font-bold text-3xl text-gold-400">{stat.number}</p>
                <p className="text-white/60 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <p className="text-white/40 text-xs tracking-widest uppercase">Scroll</p>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/40" />
        </div>
      </div>
    </section>
  );
}
